import { Classes, EditableText, Intent, Position } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Tooltip } from 'vet-bones/components';
import { isEqual, isTesting } from 'vet-bones/utils';

import { useDidValueChange } from 'src/utils/hooks/useDidValueChange';

export type EditableLabelProps = {
  className?: string;
  errorIntent?: Intent;
  value: string;
  /** whether to call onSetValue on every change or just after losing focus */
  setValueOnChange?: boolean;
  onBlur?: (newLabel: string) => void;
  onSetValue: (newLabel: string) => void;
  onValidate?: (newLabel: string) => string;
  disabled?: boolean;
  canSetEmptyString?: boolean;
  monospaceText?: boolean;
} & Pick<React.ComponentProps<typeof EditableText>, 'placeholder' | 'minWidth'>;

export const EditableLabel: React.VFC<EditableLabelProps> = ({
  className,
  errorIntent = Intent.DANGER,
  value,
  setValueOnChange,
  onBlur,
  onSetValue,
  onValidate = () => '',
  placeholder,
  minWidth,
  disabled = false,
  canSetEmptyString = false,
  monospaceText = false,
}) => {
  const [inputValue, setInputValue] = React.useState<string>(value || '');
  const [invalid, setInvalid] = React.useState<string>(onValidate(value) || '');
  const [isHovered, setIsHovered] = React.useState(false);
  const didValueChange = useDidValueChange(value, isEqual);
  React.useEffect(() => {
    if (didValueChange) {
      setInputValue(value);
      setInvalid(onValidate(value));
    }
  }, [didValueChange, onValidate, value]);

  const onCancel = React.useCallback(() => {
    setInputValue(value);
    setInvalid(onValidate(value));
  }, [onValidate, value]);

  const onChange = React.useCallback(
    (newValue: string) => {
      setInputValue(newValue);
      setInvalid(onValidate(newValue.trim()));
      if (setValueOnChange) {
        onSetValue(newValue);
      }
    },
    [onSetValue, onValidate, setValueOnChange]
  );

  const onConfirm = React.useCallback(
    (newValue: string) => {
      const newLabel = newValue.trim();
      const newInvalid = onValidate(newLabel);
      let blurValue = newValue;
      if (newLabel === '' && canSetEmptyString) {
        onSetValue(newLabel);
        blurValue = newLabel;
      } else if (newLabel && !newInvalid) {
        setInputValue(newLabel);
        onSetValue(newLabel);
        blurValue = newLabel;
      } else if (!setValueOnChange) {
        // Revert to the prior value if invalid or only empty string / whitespace is left
        setInputValue(value);
        setInvalid(onValidate(value));
        blurValue = value;
      }
      onBlur?.(blurValue);
    },
    [onBlur, onSetValue, onValidate, setValueOnChange, value, canSetEmptyString]
  );

  return (
    <div
      className="bp4-popover-wrapper"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <EditableText
        // the way blueprintjs swaps an input for a span makes testing awkward,
        // so only use an input element when testing
        alwaysRenderInput={isTesting()}
        className={classNames(
          'sd-editable-label',
          className,
          monospaceText ? Classes.MONOSPACE_TEXT : null
        )}
        disabled={disabled}
        intent={invalid ? errorIntent : Intent.NONE}
        minWidth={minWidth}
        onCancel={onCancel}
        onChange={onChange}
        onConfirm={onConfirm}
        placeholder={placeholder}
        selectAllOnFocus
        value={inputValue}
      />
      <Tooltip
        autoFocus={false}
        content={invalid}
        disabled={!invalid}
        isOpen={Boolean(isHovered && invalid)}
        position={Position.BOTTOM}
        usePortal={!isTesting()}
      />
    </div>
  );
};
